/**
 * Hollow candles — `PlotPalette.candleStyle: "hollow"` (owner, 2026-10-05).
 *
 * Ported from the WPF app's `CandleStickStrokeFillPaletteProvider`, which is the
 * TC2000 convention: **hollow vs filled reads the day, the colour reads the
 * change.**
 *
 * | | rule |
 * |---|---|
 * | outline | up when close > the previous close, down when below; equal → the day's own direction |
 * | body, close > open | empty |
 * | body, close <= open | filled, up when close >= the previous close, else down |
 *
 * Two departures from the WPF code, both deliberate:
 *
 * - **An empty body is transparent, not black.** Black reads as empty only on a
 *   dark background; on a light theme it is a solid black candle.
 * - **The second bar has a previous close.** The WPF code tests `index > 1` and
 *   so colours bar 1 by the standard rule — an off-by-one, not a decision.
 *
 * The first bar has no previous close, so both halves fall back to the day's own
 * direction, which is what "standard" would draw there.
 *
 * Pure, so it is tested without SciChart: the shell hands in packed ARGB colours
 * and reads the arrays back per bar.
 */

export interface CandleArgb {
  up: number;
  down: number;
  upFill: number;
  downFill: number;
}

/** A transparent body — the "hollow". */
export const HOLLOW_FILL = 0x00000000;

export interface CandleColors {
  stroke: number[];
  fill: number[];
}

export function hollowCandleColors(
  open: (index: number) => number,
  close: (index: number) => number,
  length: number,
  colors: CandleArgb,
): CandleColors {
  const stroke = new Array<number>(length);
  const fill = new Array<number>(length);
  for (let i = 0; i < length; i++) {
    const c = close(i);
    const o = open(i);
    const dayUp = c >= o;
    const prev = i > 0 ? close(i - 1) : undefined;

    stroke[i] =
      prev === undefined || c === prev ? (dayUp ? colors.up : colors.down) : c > prev ? colors.up : colors.down;

    if (c > o) fill[i] = HOLLOW_FILL;
    else if (prev === undefined) fill[i] = colors.downFill;
    else fill[i] = c >= prev ? colors.upFill : colors.downFill;
  }
  return { stroke, fill };
}

/** The standard rule: up when close >= open, outline and body alike. */
export function standardCandleColors(
  open: (index: number) => number,
  close: (index: number) => number,
  length: number,
  colors: CandleArgb,
): CandleColors {
  const stroke = new Array<number>(length);
  const fill = new Array<number>(length);
  for (let i = 0; i < length; i++) {
    const up = close(i) >= open(i);
    stroke[i] = up ? colors.up : colors.down;
    fill[i] = up ? colors.upFill : colors.downFill;
  }
  return { stroke, fill };
}

/**
 * A paint-bar indicator's column (`Plot.type: "paint"`, owner 2026-10-05):
 * where it is positive the body takes `up`, where negative `down`, and at 0 or
 * no value the candle keeps its own colour. HCD/LCD first.
 */
export interface CandlePaint {
  values: readonly (number | null | undefined)[];
  up: number;
  down: number;
}

/**
 * Every candle's outline and body: the rule (`standard` or `hollow`), then each
 * paint laid over the **body** in order, so a later paint wins a bar two of them
 * mark. The outline is never painted — it still says which way the bar went,
 * which is the half of the candle a paint does not speak for.
 *
 * A paint shorter than the bars is aligned on the **last** bar, not the first:
 * an indicator column whose warm-up was cut has fewer rows than the candles,
 * and it is the recent end that both describe.
 */
export function candleColors(
  open: (index: number) => number,
  close: (index: number) => number,
  length: number,
  colors: CandleArgb,
  style: "standard" | "hollow",
  paints: readonly CandlePaint[] = [],
): CandleColors {
  const out =
    style === "hollow"
      ? hollowCandleColors(open, close, length, colors)
      : standardCandleColors(open, close, length, colors);
  for (const paint of paints) {
    const offset = length - paint.values.length;
    for (let i = Math.max(0, offset); i < length; i++) {
      const v = paint.values[i - offset];
      // NaN included: `NaN > 0` is false, so it would otherwise paint down.
      if (v === null || v === undefined || !Number.isFinite(v) || v === 0) continue;
      out.fill[i] = v > 0 ? paint.up : paint.down;
    }
  }
  return out;
}
